/**
 * Helpers for drawing canvas UI that must keep a constant size in screen
 * pixels regardless of the current zoom level (e.g. mindmap hover buttons,
 * collapse chevron, comment indicators).
 *
 * The static canvas already has `context.scale(zoom, zoom)` applied by the
 * renderer setup, so scene coordinates are drawn at `zoom` px per scene unit.
 * To draw something at a constant screen size, divide dimensions by `zoom`
 * or use `withScreenScale` which inverts the scale on a translated origin.
 */

/**
 * Runs `draw` in a context that is anchored at (anchorX, anchorY) in scene
 * coordinates and scaled so `1 unit` inside `draw` equals `1 screen pixel`.
 *
 * Example:
 *   withScreenScale(ctx, zoom, x, y, (ctx) => {
 *     ctx.beginPath();
 *     ctx.arc(0, 0, 12, 0, Math.PI * 2); // 12 screen pixels
 *     ctx.fill();
 *   });
 */
export const withScreenScale = (
  context: CanvasRenderingContext2D,
  zoom: number,
  anchorX: number,
  anchorY: number,
  draw: (context: CanvasRenderingContext2D) => void,
): void => {
  context.save();
  context.translate(anchorX, anchorY);
  context.scale(1 / zoom, 1 / zoom);
  draw(context);
  context.restore();
};

/** Convert a screen-pixel value to scene units at the given zoom. */
export const screenPx = (value: number, zoom: number): number => value / zoom;

/**
 * Euclidean circle hit-test in a single coordinate space.
 * All parameters must be in the same units (scene or screen).
 */
export const hitCircle = (
  px: number,
  py: number,
  cx: number,
  cy: number,
  radius: number,
): boolean => {
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= radius * radius;
};
